import { format, isToday, isYesterday } from 'date-fns';
import { useState } from 'react';
import { tv } from 'tailwind-variants';

import Image from '@cartwhl/ui/atoms/Image';
import Loading from '@cartwhl/ui/atoms/Loading';
import Video from '@cartwhl/ui/atoms/Video';
import Text from '@cartwhl/ui/base/TextNew';
import CheckboxButton from '@cartwhl/ui/input/CheckboxButton';

import { noticesStore } from 'stores/global';

import type {
  KeyPoseGenerationHistoryEntry,
  KeyPoseGenerationHistoryStatus,
} from '../../keyPoses';

const STATUS_LABELS: Record<KeyPoseGenerationHistoryStatus, string> = {
  queued: 'Queued',
  regenerating: 'Regenerating',
  generated: 'Generated',
  'updating-timeline': 'Updating timeline',
  applied: 'Applied',
  'apply-failed': 'Apply failed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_CLASS_NAMES: Record<KeyPoseGenerationHistoryStatus, string> = {
  queued: 'bg-secondary text-secondary',
  regenerating: 'bg-blue-50 text-white',
  generated: 'bg-purple-50 text-white',
  'updating-timeline': 'bg-blue text-white',
  applied: 'bg-green-10 text-green-50',
  'apply-failed': 'bg-red-10 text-red-50',
  failed: 'bg-red-10 text-red-50',
  cancelled: 'bg-secondary text-tertiary',
};

const historyPanelItem = tv({
  base: 'bg-primary flex flex-col gap-3 rounded-2xl border p-3 transition-colors',
  variants: {
    appliedToScene: {
      true: 'border-blue ring-blue ring-1',
      false: 'border-primary',
    },
    applyEnabled: {
      true: 'cursor-pointer hover:border-blue-50',
      false: 'cursor-default',
    },
  },
  defaultVariants: {
    appliedToScene: false,
    applyEnabled: false,
  },
});

interface HistoryPanelProps {
  actorLabel: string | null;
  canApplyHistoryGeneration: boolean;
  hasPendingGeneration: boolean;
  historyEntries: KeyPoseGenerationHistoryEntry[];
  loading: boolean;
  appliedHistoryJobIDs: string[];
  ghostSelectableJobIDs: string[];
  ghostSelectionVisible: boolean;
  ghostSelectionLoadingJobID: string | null;
  selectedGhostHistoryJobID: string | null;
  onApplyHistoryGeneration: (
    entry: KeyPoseGenerationHistoryEntry,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  onSelectGhostHistoryEntry: (
    entry: KeyPoseGenerationHistoryEntry,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  onClearGhostHistoryEntry: () => void;
}

export default function HistoryPanel({
  actorLabel,
  appliedHistoryJobIDs,
  canApplyHistoryGeneration,
  ghostSelectableJobIDs,
  ghostSelectionVisible,
  ghostSelectionLoadingJobID,
  hasPendingGeneration,
  historyEntries,
  loading,
  onApplyHistoryGeneration,
  onClearGhostHistoryEntry,
  onSelectGhostHistoryEntry,
  selectedGhostHistoryJobID,
}: HistoryPanelProps) {
  const [expandedErrorJobIDs, setExpandedErrorJobIDs] = useState<string[]>([]);
  const headline = actorLabel ? `${actorLabel} generations` : 'Generations';

  return (
    <div className="flex flex-col gap-3">
      <Text as="h2" token="14/medium" className="text-primary">
        {headline}
      </Text>
      {loading ? (
        <div
          className="bg-secondary flex items-center gap-2 rounded-xl p-4"
          role="status"
        >
          <Text as="div" token="14" className="text-secondary text-pretty">
            Loading generations…
          </Text>
        </div>
      ) : historyEntries.length === 0 ? (
        <Text
          as="div"
          token="14"
          className="text-secondary bg-secondary text-pretty rounded-xl p-4"
        >
          No regenerations for this scene yet.
        </Text>
      ) : (
        <div className="flex flex-col gap-2">
          {historyEntries.map((entry) => (
            <HistoryPanelItem
              key={entry.jobID}
              entry={entry}
              appliedToScene={appliedHistoryJobIDs.includes(entry.jobID)}
              applyDisabled={hasPendingGeneration}
              canApplyHistoryGeneration={canApplyHistoryGeneration}
              errorExpanded={expandedErrorJobIDs.includes(entry.jobID)}
              ghostSelectable={ghostSelectableJobIDs.includes(entry.jobID)}
              ghostSelectionLoading={ghostSelectionLoadingJobID === entry.jobID}
              ghostSelectionVisible={ghostSelectionVisible}
              ghostSelected={selectedGhostHistoryJobID === entry.jobID}
              onApply={(historyEntry) => {
                void applyHistoryEntry({
                  entry: historyEntry,
                  onApplyHistoryGeneration,
                });
              }}
              onSelectGhost={(historyEntry) => {
                void selectGhostHistoryEntry({
                  entry: historyEntry,
                  onSelectGhostHistoryEntry,
                });
              }}
              onClearGhost={onClearGhostHistoryEntry}
              onToggleError={() => {
                setExpandedErrorJobIDs((currentJobIDs) =>
                  currentJobIDs.includes(entry.jobID)
                    ? currentJobIDs.filter((jobID) => jobID !== entry.jobID)
                    : [...currentJobIDs, entry.jobID],
                );
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface HistoryPanelItemProps {
  appliedToScene: boolean;
  applyDisabled: boolean;
  canApplyHistoryGeneration: boolean;
  entry: KeyPoseGenerationHistoryEntry;
  errorExpanded: boolean;
  ghostSelectable: boolean;
  ghostSelected: boolean;
  ghostSelectionLoading: boolean;
  ghostSelectionVisible: boolean;
  onApply: (entry: KeyPoseGenerationHistoryEntry) => void;
  onClearGhost: () => void;
  onSelectGhost: (entry: KeyPoseGenerationHistoryEntry) => void;
  onToggleError: () => void;
}

function HistoryPanelItem({
  appliedToScene,
  applyDisabled,
  canApplyHistoryGeneration,
  entry,
  errorExpanded,
  ghostSelectable,
  ghostSelected,
  ghostSelectionLoading,
  ghostSelectionVisible,
  onApply,
  onClearGhost,
  onSelectGhost,
  onToggleError,
}: HistoryPanelItemProps) {
  const canApply =
    canApplyHistoryGeneration &&
    !appliedToScene &&
    (entry.status === 'generated' || entry.status === 'apply-failed');
  const isUpdatingTimeline = entry.status === 'updating-timeline';
  const applyActionDisabled = !canApply || applyDisabled || isUpdatingTimeline;
  const cardApplyEnabled = !applyActionDisabled;
  const ghostActionDisabled =
    !ghostSelectionVisible || !ghostSelectable || ghostSelectionLoading;
  const showActionButtons = entry.status !== 'failed';
  const showStatusChip = isHistoryStatusChipVisible(entry.status);

  return (
    <article
      className={historyPanelItem({
        appliedToScene,
        applyEnabled: cardApplyEnabled,
      })}
      aria-label={cardApplyEnabled ? 'Apply generation to scene' : undefined}
      role={cardApplyEnabled ? 'button' : undefined}
      tabIndex={cardApplyEnabled ? 0 : undefined}
      onClick={() => {
        if (cardApplyEnabled) {
          onApply(entry);
        }
      }}
      onKeyDown={(event) => {
        if (!cardApplyEnabled) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onApply(entry);
        }
      }}
    >
      <div className="relative flex gap-4">
        <div className="bg-tertiary flex h-[72px] w-[97px] flex-none items-center justify-center overflow-hidden rounded-lg">
          {entry.thumbnailUrl ? (
            <Video
              src={entry.thumbnailUrl}
              className="h-full w-full object-cover"
              mode="autoplay"
              crossOrigin="use-credentials"
            />
          ) : (
            <></>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Text
            token="14/medium"
            className="text-primary line-clamp-2 text-pretty"
          >
            <HistoryEntryTitle entry={entry} />
          </Text>

          <Text token="14/medium" className="text-tertiary">
            <HistoryEntrySubtitle entry={entry} />
          </Text>

          {isUpdatingTimeline ? (
            <span
              aria-label="Updating timeline"
              className="text-blue absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center"
              role="status"
            >
              <Loading className="h-4 w-4" strokeWidth={3} />
            </span>
          ) : showStatusChip ? (
            <HistoryStatusChip status={entry.status} />
          ) : null}

          {entry.errorMessage ? (
            <button
              aria-expanded={errorExpanded}
              className="w-full cursor-pointer text-left"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onToggleError();
              }}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Text
                token="12"
                className={`text-pretty text-red-50 ${errorExpanded ? '' : 'line-clamp-3'}`}
              >
                {entry.errorMessage}
              </Text>
            </button>
          ) : null}

          {showActionButtons ? (
            <div className="flex w-full justify-end">
              <CheckboxButton
                checked={ghostSelected}
                disabled={ghostActionDisabled}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                onCheckedChange={(checked) => {
                  if (checked && !ghostActionDisabled) {
                    onSelectGhost(entry);
                    return;
                  }

                  if (!checked && ghostSelected) {
                    onClearGhost();
                  }
                }}
              >
                {ghostSelectionLoading ? 'Loading…' : 'Compare'}
              </CheckboxButton>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function HistoryEntryTitle({
  entry,
}: {
  entry: KeyPoseGenerationHistoryEntry;
}) {
  if (entry.isBaseline) {
    return <>Original motion</>;
  }

  if (entry.prompt?.trim()) {
    return <>{entry.prompt.trim()}</>;
  }

  return <ShortRelativeDate date={entry.createdAt} />;
}

function HistoryEntrySubtitle({
  entry,
}: {
  entry: KeyPoseGenerationHistoryEntry;
}) {
  const keyPoseLabel =
    entry.userKeyPoseCount != null && entry.userKeyPoseCount > 0
      ? `${entry.userKeyPoseCount} key ${entry.userKeyPoseCount === 1 ? 'pose' : 'poses'}`
      : null;

  if (keyPoseLabel) {
    return (
      <>
        {keyPoseLabel}
        {' · '}
        <TimeOfDay date={entry.createdAt} />
      </>
    );
  }

  return <TimeOfDay date={entry.createdAt} />;
}

function isHistoryStatusChipVisible(status: KeyPoseGenerationHistoryStatus) {
  return (
    status !== 'generated' &&
    status !== 'applied' &&
    status !== 'updating-timeline'
  );
}

function ShortRelativeDate({ date: value }: { date: string }) {
  const date = parseHistoryDate(value);
  if (!date) {
    return <>Unknown date</>;
  }

  return <>{formatShortRelativeDate(date)}</>;
}

function TimeOfDay({ date: value }: { date: string }) {
  const date = parseHistoryDate(value);
  if (!date) {
    return <>Unknown time</>;
  }

  return <>{formatTimeOfDay(date)}</>;
}

function HistoryStatusChip({
  status,
}: {
  status: KeyPoseGenerationHistoryStatus;
}) {
  return (
    <span
      className={`absolute -right-1 -top-1 inline-flex h-[20px] shrink-0 items-center justify-center rounded-full px-2 ${STATUS_CLASS_NAMES[status]}`}
    >
      <Text token="12/medium" className="text-inherit">
        {STATUS_LABELS[status]}
      </Text>
    </span>
  );
}

async function selectGhostHistoryEntry({
  entry,
  onSelectGhostHistoryEntry,
}: {
  entry: KeyPoseGenerationHistoryEntry;
  onSelectGhostHistoryEntry: (
    entry: KeyPoseGenerationHistoryEntry,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}) {
  const result = await onSelectGhostHistoryEntry(entry);
  if (!result.ok) {
    noticesStore.error(result.error);
  }
}

async function applyHistoryEntry({
  entry,
  onApplyHistoryGeneration,
}: {
  entry: KeyPoseGenerationHistoryEntry;
  onApplyHistoryGeneration: (
    entry: KeyPoseGenerationHistoryEntry,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}) {
  const result = await onApplyHistoryGeneration(entry);

  if (result.ok) {
    // No feedback when successful, seeing the loader disappear and the motion to start as expected is enough.
  } else {
    noticesStore.error(result.error);
  }
}

function parseHistoryDate(value: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }

  return date;
}

function formatShortRelativeDate(date: Date) {
  if (isToday(date)) {
    return 'Today';
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  const now = new Date();
  if (date.getFullYear() === now.getFullYear()) {
    return format(date, 'MMM d');
  }

  return format(date, 'MMM d, yyyy');
}

function formatTimeOfDay(date: Date) {
  return format(date, 'h:mmaaa');
}
